import React, { useMemo, useState } from "react";
import { Select } from "antd";
import { Col, Form, InputGroup, Row } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import BeatLoader from "react-spinners/BeatLoader";
import { Button, Input, SubTitle } from "../../Components/CustomComponents";
import { useGenerateDeeplinkUrlMutation } from "../../Data/services/deeplinkApi";
import { useGetPostsListQuery } from "../../Data/services/postApi";
import Toast, { SuccessToast } from "../../Hooks/useToast";
import styles from "./styles/generateDeeplinkUrlScreen.module.scss";

const GenerateDeeplinkUrlScreen = () => {
  const [generatedUrl, setGeneratedUrl] = useState("");

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {
      postId: "",
      alias: "",
      campaign: "",
      duration: "",
    },
  });

  const { data: postsData, isLoading: isPostsLoading } = useGetPostsListQuery();
  const [generateDeeplinkUrl, { isLoading }] = useGenerateDeeplinkUrlMutation();

  const postOptions = useMemo(() => {
    const posts = postsData?.data || [];

    return posts.map((post) => {
      const userName = [post?.user?.firstname, post?.user?.lastname]
        .filter(Boolean)
        .join(" ");
      const description = post?.description || post?.content || "";
      const truncatedDescription =
        description.length > 40
          ? `${description.slice(0, 40)}...`
          : description;

      const labelParts = [
        `#${post.id}`,
        userName || null,
        truncatedDescription || null,
      ].filter(Boolean);

      return {
        value: post.id,
        label: labelParts.join(" — "),
      };
    });
  }, [postsData]);

  const getDeeplinkFromResponse = (responseData) => {
    const data = responseData?.data;

    if (typeof data === "string") {
      return data;
    }

    if (data?.deeplinkUrl) {
      return data.deeplinkUrl;
    }

    if (data?.url) {
      return data.url;
    }

    if (data) {
      return JSON.stringify(data, null, 2);
    }

    return "";
  };

  const handleGenerateDeeplinkUrl = async (formData) => {
    try {
      const payload = {};

      if (formData.postId) {
        payload.postId = Number(formData.postId);
      }

      const response = await generateDeeplinkUrl(payload);

      if (response.data?.code === 200) {
        const deeplink = getDeeplinkFromResponse(response.data);
        setGeneratedUrl(deeplink);
        SuccessToast(
          response?.data?.message || "Deeplink URL generated successfully",
        );
      } else {
        Toast(
          response?.data?.message || "Failed to generate deeplink URL",
          "error",
        );
      }
    } catch (error) {
      console.log("error:", error);
      if (error?.response && error?.response?.data?.message) {
        Toast(error?.response?.data?.message, "error");
      } else {
        Toast("An error occurred while generating deeplink URL", "error");
      }
    }
  };

  const handleCopyUrl = async () => {
    if (!generatedUrl) {
      return;
    }

    try {
      await navigator.clipboard.writeText(generatedUrl);
      SuccessToast("Deeplink URL copied to clipboard");
    } catch {
      Toast("Failed to copy deeplink URL", "error");
    }
  };

  return (
    <div className={styles.MainContainer}>
      {errors?.duration?.message
        ? Toast(errors.duration?.message, "error")
        : null}

      <div className={styles.headerContainer}>
        <div>
          <SubTitle text={"Generate Deeplinking URL"} classes="primary-text" />
        </div>
        <Row>
          <Col md={12}>
            <Button
              classes={`${styles.cardBtn} ${isLoading ? "bg-dark-subtle" : ""}`}
              text={isLoading ? <BeatLoader size={10} /> : "Generate"}
              onClick={handleSubmit(handleGenerateDeeplinkUrl)}
              disabled={isLoading || isPostsLoading}
            />
          </Col>
        </Row>
      </div>

      <div className="mt-3">
        <Form>
          <Controller
            control={control}
            name="postId"
            render={({ field: { onChange, value } }) => (
              <div className="mb-3">
                <Select
                  className={styles.postSelect}
                  showSearch
                  allowClear
                  optionFilterProp="label"
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  placeholder={
                    isPostsLoading
                      ? "Loading posts..."
                      : "Search & select post (optional)"
                  }
                  options={postOptions}
                  value={value || undefined}
                  onChange={(selectedValue) => onChange(selectedValue ?? "")}
                  loading={isPostsLoading}
                  disabled={isPostsLoading}
                  notFoundContent="No posts found"
                />
              </div>
            )}
          />
        </Form>
      </div>

      {generatedUrl ? (
        <div className={styles.resultContainer}>
          <div className={styles.resultLabel}>Generated Deeplink URL</div>
          <InputGroup className="mb-3">
            <Input
              placeholder="Generated URL will appear here"
              type="textarea"
              as="textarea"
              rows={4}
              value={generatedUrl}
              disable={true}
            />
          </InputGroup>
          <Button text="Copy URL" onClick={handleCopyUrl} />
        </div>
      ) : null}
    </div>
  );
};

export default GenerateDeeplinkUrlScreen;
